/**
 * Reducer for the contacts feature.
 */
import ReducerRegistry from '../base/redux/ReducerRegistry';
import { CONTACTS_LOADED, CONTACTS_LOADING, CONTACTS_LOAD_FAILED, CONTACT_ADDED, CONTACT_UPDATED, CONTACT_FAVORITE_CONFIRMED, CONTACT_DELETED, CONTACT_DETAIL_LOADED, CONTACT_DETAIL_LOADING, CONTACT_DETAIL_FAILED, CONTACT_SELECTED, CONTACT_DETAIL_CLOSED, CONTACT_NOTE_ADDED, CONTACT_NOTE_UPDATED, CONTACT_NOTE_DELETED, CONTACT_GROUPS_LOADED, CONTACT_GROUP_ADDED, CONTACT_GROUP_DELETED, CONTACT_BLOCKED_LOADED, CONTACTS_SYNC_RECEIVED, CONTACTS_SYNC_TOKEN_UPDATED, CONTACTS_SET_SEARCH, CONTACTS_SET_TAB, CONTACTS_SET_GROUP_FILTER, CONTACTS_ERROR, CONTACTS_CLEAR_ERROR } from './actionTypes';
const INITIAL_STATE = {
    contacts: [],
    groups: [],
    blocked: [],
    selectedContactId: null,
    contactDetail: null,
    isLoading: false,
    listRequestId: null,
    isDetailLoading: false,
    detailRequestId: null,
    detailViewId: null,
    detailScope: null,
    detailError: null,
    search: '',
    activeTab: 'all',
    selectedGroupId: null,
    syncToken: null,
    error: null
};
export function contactsReducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case CONTACTS_LOADING:
            return { ...state, isLoading: true, listRequestId: action.requestId || null, error: null };
        case CONTACTS_LOADED:
            if (!action.requestId || state.listRequestId !== action.requestId)
                return state;
            return {
                ...state,
                contacts: action.contacts || [],
                isLoading: false
            };
        case CONTACTS_LOAD_FAILED:
            if (!action.requestId || state.listRequestId !== action.requestId)
                return state;
            return { ...state, isLoading: false, error: action.error || 'Contacts could not be loaded. Try again.' };
        case CONTACT_ADDED:
            if (action.contact) {
                return {
                    ...state,
                    contacts: [...state.contacts, action.contact]
                };
            }
            return state;
        case CONTACT_UPDATED:
            if (action.contact) {
                return {
                    ...state,
                    contacts: state.contacts.map(c => c.id === action.contact.id ? action.contact : c),
                    contactDetail: state.contactDetail?.contact.id === action.contact.id
                        ? { ...state.contactDetail, contact: action.contact }
                        : state.contactDetail
                };
            }
            return state;
        case CONTACT_FAVORITE_CONFIRMED: {
            if (!action.contact)
                return state;
            const contact = action.contact;
            const matchesDetail = state.contactDetail?.contact.id === contact.id;
            return {
                ...state,
                contacts: state.contacts.map(row => row.id === contact.id ? contact : row)
                    .filter(row => state.activeTab !== 'favorites' || row.is_favorite === 1),
                contactDetail: matchesDetail && state.contactDetail
                    ? { ...state.contactDetail, contact } : state.contactDetail,
                // An older detail read must not erase the confirmed favorite.
                detailRequestId: matchesDetail ? null : state.detailRequestId,
                isDetailLoading: matchesDetail ? false : state.isDetailLoading
            };
        }
        case CONTACT_DELETED: {
            const selected = state.selectedContactId === action.contactId;
            return {
                ...state,
                contacts: state.contacts.filter(c => c.id !== action.contactId),
                // An older list snapshot must not resurrect this confirmed deletion.
                listRequestId: null,
                isLoading: false,
                contactDetail: state.contactDetail?.contact.id === action.contactId ? null : state.contactDetail,
                selectedContactId: selected ? null : state.selectedContactId,
                detailRequestId: selected ? null : state.detailRequestId,
                detailViewId: selected ? null : state.detailViewId,
                detailScope: selected ? null : state.detailScope,
                detailError: selected ? null : state.detailError,
                isDetailLoading: selected ? false : state.isDetailLoading
            };
        }
        case CONTACT_DETAIL_LOADING:
            return { ...state, selectedContactId: action.contactId || null, detailRequestId: action.requestId || null,
                detailViewId: action.viewId || null, detailError: null, isDetailLoading: true,
                contactDetail: state.selectedContactId === action.contactId ? state.contactDetail : null };
        case CONTACT_DETAIL_FAILED:
            if (state.detailRequestId !== action.requestId || state.selectedContactId !== action.contactId)
                return state;
            return { ...state, isDetailLoading: false, detailError: action.error || 'Contact details are unavailable.' };
        case CONTACT_DETAIL_LOADED:
            if (state.detailRequestId !== action.requestId || state.selectedContactId !== action.contactId)
                return state;
            if (action.contactDetail) {
                return {
                    ...state,
                    contactDetail: {
                        contact: action.contactDetail.contact,
                        timeline: action.contactDetail.timeline,
                        notes: action.contactDetail.notes
                    },
                    isDetailLoading: false,
                    detailScope: action.scope || null
                };
            }
            return { ...state, isDetailLoading: false };
        case CONTACT_SELECTED:
            return { ...state, selectedContactId: action.contactId || null };
        case CONTACT_DETAIL_CLOSED:
            return {
                ...state,
                contactDetail: null,
                selectedContactId: null,
                isDetailLoading: false,
                detailRequestId: null, detailViewId: null, detailScope: null, detailError: null
            };
        case CONTACT_NOTE_ADDED:
            if (action.note && state.contactDetail && state.contactDetail.contact.id === action.contactId && state.detailViewId === action.viewId) {
                return {
                    ...state,
                    detailRequestId: null, isDetailLoading: false,
                    contactDetail: {
                        ...state.contactDetail,
                        notes: [action.note, ...state.contactDetail.notes.filter(note => note.id !== action.note.id)],
                        timeline: [
                            {
                                type: 'note',
                                id: action.note.id,
                                timestamp: action.note.created_at,
                                data: { content: action.note.content, author_id: action.note.author_id }
                            },
                            ...state.contactDetail.timeline.filter(item => item.type !== 'note' || item.id !== action.note.id)
                        ]
                    }
                };
            }
            return state;
        case CONTACT_NOTE_UPDATED:
            if (action.notePatch && state.contactDetail && state.contactDetail.contact.id === action.contactId && state.detailViewId === action.viewId) {
                return {
                    ...state,
                    detailRequestId: null, isDetailLoading: false,
                    contactDetail: {
                        ...state.contactDetail,
                        notes: state.contactDetail.notes.map(n => n.id === action.notePatch.id ? { ...n, ...action.notePatch } : n),
                        timeline: state.contactDetail.timeline.map(t => t.type === 'note' && t.id === action.notePatch.id
                            ? { ...t, data: { ...t.data, content: action.notePatch.content } }
                            : t)
                    }
                };
            }
            return state;
        case CONTACT_NOTE_DELETED:
            if (action.noteId && state.contactDetail && state.contactDetail.contact.id === action.contactId && state.detailViewId === action.viewId) {
                return {
                    ...state,
                    detailRequestId: null, isDetailLoading: false,
                    contactDetail: {
                        ...state.contactDetail,
                        notes: state.contactDetail.notes.filter(n => n.id !== action.noteId),
                        timeline: state.contactDetail.timeline.filter(t => !(t.type === 'note' && t.id === action.noteId))
                    }
                };
            }
            return state;
        case CONTACT_GROUPS_LOADED:
            return { ...state, groups: action.groups || [] };
        case CONTACT_GROUP_ADDED:
            if (action.group) {
                return { ...state, groups: [...state.groups, action.group] };
            }
            return state;
        case CONTACT_GROUP_DELETED:
            return {
                ...state,
                groups: state.groups.filter(g => g.id !== action.groupId)
            };
        case CONTACT_BLOCKED_LOADED:
            return { ...state, blocked: action.blocked || [] };
        case CONTACTS_SYNC_RECEIVED: {
            if (!action.changes)
                return state;
            let updatedContacts = [...state.contacts];
            for (const change of action.changes) {
                if (change.action === 'delete') {
                    updatedContacts = updatedContacts.filter(c => c.id !== change.entity_id);
                }
                else if (change.action === 'create' && change.snapshot) {
                    const exists = updatedContacts.some(c => c.id === change.entity_id);
                    if (!exists) {
                        updatedContacts.push(change.snapshot);
                    }
                }
                else if (change.action === 'update' && change.snapshot) {
                    updatedContacts = updatedContacts.map(c => c.id === change.entity_id ? { ...c, ...change.snapshot } : c);
                }
            }
            return { ...state, contacts: updatedContacts };
        }
        case CONTACTS_SYNC_TOKEN_UPDATED:
            return { ...state, syncToken: action.token || null };
        case CONTACTS_SET_SEARCH:
            return { ...state, search: action.search || '' };
        case CONTACTS_SET_TAB:
            return { ...state, activeTab: action.tab || 'all' };
        case CONTACTS_SET_GROUP_FILTER:
            return { ...state, selectedGroupId: action.groupIdFilter ?? null };
        case CONTACTS_ERROR:
            return {
                ...state,
                error: action.error || 'Unknown error',
                isLoading: false
            };
        case CONTACTS_CLEAR_ERROR:
            return { ...state, error: null };
        default:
            return state;
    }
}
ReducerRegistry.register('features/contacts', contactsReducer);
